import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  X,
  Play,
  UserMinus,
  UserPlus,
  CreditCard,
  TrendingUp,
  Trash2,
  Percent,
  Zap,
} from "lucide-react";
import { apiFetch, API } from "@/utils/api";

const ADJUSTMENT_TYPES = [
  { value: "remove_client", label: "Perder cliente", icon: UserMinus },
  { value: "add_revenue", label: "Nuevo ingreso", icon: TrendingUp },
  { value: "add_expense", label: "Nuevo gasto", icon: CreditCard },
  { value: "remove_subscription", label: "Quitar suscripción", icon: Trash2 },
  { value: "cost_increase_pct", label: "Subida de costes %", icon: Percent },
];

const PRESETS = [
  {
    label: "¿Y si pierdo mi mayor cliente?",
    icon: UserMinus,
    getAdjustment: (clients) => {
      if (!clients.length) return null;
      const top = clients.sort((a, b) => (b.revenue || 0) - (a.revenue || 0))[0];
      return { type: "remove_client", client_id: top.client_id, client_name: top.name, monthly_revenue: top.revenue || 0 };
    },
  },
  {
    label: "¿Y si contrato a alguien?",
    icon: UserPlus,
    getAdjustment: () => ({ type: "add_expense", label: "Nueva contratación", monthly: 2000 }),
  },
  {
    label: "¿Y si suben costes un 20%?",
    icon: Percent,
    getAdjustment: () => ({ type: "cost_increase_pct", percentage: 20 }),
  },
];

function formatCurrency(amount) {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amount);
}

function AdjustmentForm({ onAdd, clients, subscriptions }) {
  const [type, setType] = useState("");
  const [form, setForm] = useState({});

  const handleAdd = () => {
    if (!type) return;
    const adj = { type, ...form };
    onAdd(adj);
    setType("");
    setForm({});
  };

  return (
    <div className="flex flex-wrap gap-2 items-end">
      <div>
        <Select value={type} onValueChange={(v) => { setType(v); setForm({}); }}>
          <SelectTrigger className="w-[200px] h-8 text-xs">
            <SelectValue placeholder="Tipo de ajuste..." />
          </SelectTrigger>
          <SelectContent>
            {ADJUSTMENT_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {type === "remove_client" && (
        <Select value={form.client_id || ""} onValueChange={(v) => {
          const c = clients.find(cl => cl.client_id === v);
          setForm({ client_id: v, client_name: c?.name || v, monthly_revenue: c?.revenue || 0 });
        }}>
          <SelectTrigger className="w-[180px] h-8 text-xs">
            <SelectValue placeholder="Cliente..." />
          </SelectTrigger>
          <SelectContent>
            {clients.map((c) => (
              <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {type === "remove_subscription" && (
        <Select value={form.subscription_id || ""} onValueChange={(v) => {
          const s = subscriptions.find(sub => sub.subscription_id === v);
          setForm({ subscription_id: v, subscription_name: s?.name || v, monthly_cost: s?.price || 0 });
        }}>
          <SelectTrigger className="w-[180px] h-8 text-xs">
            <SelectValue placeholder="Suscripción..." />
          </SelectTrigger>
          <SelectContent>
            {subscriptions.map((s) => (
              <SelectItem key={s.subscription_id} value={s.subscription_id}>{s.name} ({s.price}€)</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {(type === "add_expense" || type === "add_revenue") && (
        <>
          <Input
            className="w-[140px] h-8 text-xs"
            placeholder="Descripción"
            value={form.label || ""}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
          <Input
            className="w-[100px] h-8 text-xs"
            type="number"
            placeholder="€/mes"
            value={form.monthly || ""}
            onChange={(e) => setForm({ ...form, monthly: parseFloat(e.target.value) || 0 })}
          />
        </>
      )}

      {type === "cost_increase_pct" && (
        <Input
          className="w-[80px] h-8 text-xs"
          type="number"
          placeholder="%"
          value={form.percentage || ""}
          onChange={(e) => setForm({ ...form, percentage: parseFloat(e.target.value) || 0 })}
        />
      )}

      {type && (
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleAdd}>
          <Plus className="w-3 h-3 mr-1" /> Añadir
        </Button>
      )}
    </div>
  );
}

export default function Simulator() {
  const [adjustments, setAdjustments] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    async function fetchData() {
      try {
        const [cRes, sRes] = await Promise.all([
          apiFetch(`${API}/crm/clients`),
          apiFetch(`${API}/crm/subscriptions?status=active`),
        ]);
        if (cRes.ok) setClients(await cRes.json());
        if (sRes.ok) setSubscriptions(await sRes.json());
      } catch (e) { console.error(e); }
    }
    fetchData();
  }, []);

  const addAdjustment = (adj) => setAdjustments((prev) => [...prev, adj]);
  const removeAdjustment = (index) => setAdjustments((prev) => prev.filter((_, i) => i !== index));

  const runSimulation = async () => {
    if (adjustments.length === 0) {
      toast.error("Añade al menos un ajuste");
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/cfo/simulate`, {
        method: "POST",
        body: JSON.stringify({ adjustments, forecast_months: 6 }),
      });
      if (res.ok) {
        setResult(await res.json());
      } else {
        toast.error("Error al simular");
      }
    } catch (e) {
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (preset) => {
    const adj = preset.getAdjustment(clients);
    if (adj) {
      addAdjustment(adj);
    } else {
      toast.error("No hay datos para este escenario");
    }
  };

  const adjLabel = (adj) => {
    if (adj.type === "remove_client") return `Perder: ${adj.client_name || adj.client_id}`;
    if (adj.type === "add_revenue") return `+Ingreso: ${adj.label} (${adj.monthly}€/mes)`;
    if (adj.type === "add_expense") return `+Gasto: ${adj.label} (${adj.monthly}€/mes)`;
    if (adj.type === "remove_subscription") return `Quitar: ${adj.subscription_name || adj.subscription_id}`;
    if (adj.type === "cost_increase_pct") return `Costes +${adj.percentage}%`;
    return adj.type;
  };

  return (
    <div className="space-y-4">
      {/* Presets */}
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((preset, i) => (
          <Button
            key={i}
            variant="outline"
            size="sm"
            className="text-xs h-8"
            onClick={() => applyPreset(preset)}
          >
            <preset.icon className="w-3 h-3 mr-1.5" />
            {preset.label}
          </Button>
        ))}
      </div>

      {/* Add adjustment */}
      <AdjustmentForm onAdd={addAdjustment} clients={clients} subscriptions={subscriptions} />

      {/* Active adjustments */}
      {adjustments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {adjustments.map((adj, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700"
            >
              {adjLabel(adj)}
              <button onClick={() => removeAdjustment(i)} className="hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Run button */}
      <Button
        onClick={runSimulation}
        disabled={adjustments.length === 0 || loading}
        className="bg-slate-900 hover:bg-slate-800 text-white"
        size="sm"
      >
        <Play className="w-3.5 h-3.5 mr-1.5" />
        {loading ? "Simulando..." : "Simular escenario"}
      </Button>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Impact summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-500">Cambio ingresos</p>
              <p className={`text-lg font-mono font-semibold ${result.impact.revenue_change >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {result.impact.revenue_change >= 0 ? "+" : ""}{formatCurrency(result.impact.revenue_change)}
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-500">Cambio gastos</p>
              <p className={`text-lg font-mono font-semibold ${result.impact.expense_change <= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {result.impact.expense_change >= 0 ? "+" : ""}{formatCurrency(result.impact.expense_change)}
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-500">Cambio margen</p>
              <p className={`text-lg font-mono font-semibold ${result.impact.margin_change >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {result.impact.margin_change >= 0 ? "+" : ""}{result.impact.margin_change?.toFixed(1)}%
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-500">Nuevo runway</p>
              <p className={`text-lg font-mono font-semibold ${(result.simulated.runway_months || 0) >= 3 ? "text-emerald-600" : "text-red-600"}`}>
                {result.simulated.runway_months?.toFixed(1) || "∞"} meses
              </p>
            </div>
          </div>

          {/* Forecast comparison table */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-slate-500 uppercase">
                  <th className="px-3 py-2 text-left font-semibold">Mes</th>
                  <th className="px-3 py-2 text-right font-semibold">Ingreso actual</th>
                  <th className="px-3 py-2 text-right font-semibold">Ingreso sim.</th>
                  <th className="px-3 py-2 text-right font-semibold">Gasto actual</th>
                  <th className="px-3 py-2 text-right font-semibold">Gasto sim.</th>
                  <th className="px-3 py-2 text-right font-semibold">Saldo actual</th>
                  <th className="px-3 py-2 text-right font-semibold">Saldo sim.</th>
                </tr>
              </thead>
              <tbody>
                {result.current.forecast.map((curr, i) => {
                  const sim = result.simulated.forecast[i];
                  return (
                    <tr key={curr.month} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-3 py-2 font-mono">{curr.month}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-600">{formatCurrency(curr.income)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${sim.income < curr.income ? "text-red-600" : "text-emerald-600"}`}>
                        {formatCurrency(sim.income)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-red-500">{formatCurrency(curr.expense)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${sim.expense > curr.expense ? "text-red-600" : "text-emerald-600"}`}>
                        {formatCurrency(sim.expense)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-blue-600">{formatCurrency(curr.balance)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${sim.balance < curr.balance ? "text-red-600" : "text-blue-600"}`}>
                        {formatCurrency(sim.balance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
